import type { StaffPermissions } from '@structural-climbing/auth'
import type { NavIconName } from './NavIcon'

export type NavGroup = 'frontDesk' | 'members' | 'climbing' | 'business' | 'admin'

export interface NavItem {
  labelKey: string
  path: string
  icon: NavIconName
  /** The job section the page sits in when it is not a favourite. */
  group: NavGroup
  staffKey?: keyof StaffPermissions
  /** Also shown to staff holding this one: a trainer sees the Calendar for their own classes. */
  orStaffKey?: keyof StaffPermissions
  ownerOnly?: true
  /** Hidden unless the gym's plan includes competitions. */
  needsCompetitions?: true
  /** Hidden unless a super admin switched the Products page on for the gym. */
  needsProducts?: true
}

/** Sections in sidebar order. Products sits with the desk: it is the till, used all day. */
export const NAV_GROUPS: { key: NavGroup; labelKey: string }[] = [
  { key: 'frontDesk', labelKey: 'nav.groupFrontDesk' },
  { key: 'members', labelKey: 'nav.groupMembers' },
  { key: 'climbing', labelKey: 'nav.groupClimbing' },
  { key: 'business', labelKey: 'nav.groupBusiness' },
  { key: 'admin', labelKey: 'nav.groupAdmin' },
]

export const ALL_NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    path: 'dashboard',
    icon: 'home',
    group: 'frontDesk',
    staffKey: 'dashboard',
  },
  {
    labelKey: 'nav.checkIn',
    path: 'check-in',
    icon: 'scan',
    group: 'frontDesk',
    staffKey: 'check_in',
  },
  {
    labelKey: 'nav.requests',
    path: 'requests',
    icon: 'inbox',
    group: 'frontDesk',
    staffKey: 'requests',
  },
  {
    labelKey: 'nav.products',
    path: 'products',
    icon: 'bag',
    group: 'frontDesk',
    staffKey: 'products',
    needsProducts: true,
  },
  {
    labelKey: 'nav.members',
    path: 'members',
    icon: 'users',
    group: 'members',
    staffKey: 'members',
  },
  {
    labelKey: 'nav.myMembers',
    path: 'my-members',
    icon: 'user',
    group: 'members',
    staffKey: 'my_members',
  },
  {
    labelKey: 'nav.subscriptions',
    path: 'subscriptions',
    icon: 'card',
    group: 'members',
    staffKey: 'subscriptions',
  },
  { labelKey: 'nav.plans', path: 'plans', icon: 'tag', group: 'members', staffKey: 'plans' },
  { labelKey: 'nav.news', path: 'news', icon: 'news', group: 'members', staffKey: 'news' },
  {
    labelKey: 'nav.routes',
    path: 'routes',
    icon: 'mountain',
    group: 'climbing',
    staffKey: 'routes',
  },
  {
    labelKey: 'nav.competitions',
    path: 'competitions',
    icon: 'trophy',
    group: 'climbing',
    staffKey: 'competitions',
    needsCompetitions: true,
  },
  {
    labelKey: 'nav.calendar',
    path: 'calendar',
    icon: 'calendar',
    group: 'climbing',
    staffKey: 'calendar',
    orStaffKey: 'my_members',
  },
  {
    labelKey: 'nav.statistics',
    path: 'statistics',
    icon: 'chart',
    group: 'business',
    staffKey: 'statistics',
  },
  {
    labelKey: 'nav.invoices',
    path: 'invoices',
    icon: 'doc',
    group: 'business',
    staffKey: 'invoices',
  },
  {
    labelKey: 'nav.exports',
    path: 'exports',
    icon: 'download',
    group: 'business',
    staffKey: 'exports',
  },
  { labelKey: 'nav.activity', path: 'activity', icon: 'history', group: 'admin', ownerOnly: true },
  { labelKey: 'nav.team', path: 'team', icon: 'team', group: 'admin', ownerOnly: true },
  { labelKey: 'nav.settings', path: 'settings', icon: 'gear', group: 'admin', ownerOnly: true },
]

/** Everyone starts with these pinned, in this order, until they change it. */
export const DEFAULT_FAVOURITE_PATHS = [
  'dashboard',
  'check-in',
  'requests',
  'members',
  'subscriptions',
  'calendar',
  'products',
  'routes',
  'statistics',
]
